import React from 'react';
import {
  Box,
  Button,
  Fab,
  Grid,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { VaultItemDTO } from '../../types';
import { useWarbandStore } from '../../stores/warbandStore';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { ManageVaultModal } from '../modals/ManageVaultModal';
import { SectionHeading } from './SectionHeading';
import { MagicItemCard } from './MagicItemCard';
import { MagicItemMiniCard } from './MagicItemMiniCard';
import { ScrollCard } from './ScrollCard';
import { ScrollMiniCard } from './ScrollMiniCard';
import { GrimoireCard } from './GrimoireCard';
import { GrimoireMiniCard } from './GrimoireMiniCard';
import { PotionCard } from './PotionCard';
import { PotionMiniCard } from './PotionMiniCard';
import { miniFlexSx } from '../../App';

type ItemCategory = 'general' | 'scrolls' | 'potions' | 'grimoires';

type VaultCardComponent = React.ComponentType<{
  item: VaultItemDTO;
  memberNameById: Map<string, string>;
  onDiscard: (instanceId: string) => void;
  onSell?: (instanceId: string) => void;
}>;

const CATEGORIES: { key: ItemCategory; label: string; Card: VaultCardComponent; MiniCard: VaultCardComponent }[] = [
  { key: 'general', label: 'Magic Items', Card: MagicItemCard, MiniCard: MagicItemMiniCard },
  { key: 'scrolls', label: 'Scrolls', Card: ScrollCard, MiniCard: ScrollMiniCard },
  { key: 'potions', label: 'Potions', Card: PotionCard, MiniCard: PotionMiniCard },
  { key: 'grimoires', label: 'Grimoires', Card: GrimoireCard, MiniCard: GrimoireMiniCard },
];

function categoriseItem(item: VaultItemDTO): ItemCategory {
  const id = item.id.toLowerCase();
  if (id.startsWith('scroll_')) return 'scrolls';
  if (id.startsWith('grimoire_')) return 'grimoires';
  if (item.baseItemType === 'potion') return 'potions';
  return 'general';
}

interface VaultContentsProps {
  inlineAddButton?: boolean;
  vaultModalOpen?: boolean;
  onVaultModalClose?: () => void;
}

export const VaultContents: React.FC<VaultContentsProps> = React.memo(({ inlineAddButton = false, vaultModalOpen: controlledOpen, onVaultModalClose }) => {
  const vaultItems = useWarbandStore((s) => s.vaultItems);
  const selectedWarbandId = useWarbandStore((s) => s.selectedWarbandId);
  const wizardData = useWarbandStore((s) => s.wizardData);
  const apprenticeData = useWarbandStore((s) => s.apprenticeData);
  const soldiersData = useWarbandStore((s) => s.soldiersData);
  const isMinified = useWarbandStore((s) => s.isMinified);
  const updateVaultItems = useDirtyTrackingStore((s) => s.updateVaultItems);
  const sellVaultItem = useDirtyTrackingStore((s) => s.sellVaultItem);
  const [activeTab, setActiveTab] = React.useState(0);
  const [localVaultModalOpen, setLocalVaultModalOpen] = React.useState(false);

  const isControlled = controlledOpen !== undefined;
  const vaultModalOpen = isControlled ? controlledOpen : localVaultModalOpen;
  const handleVaultModalClose = isControlled ? onVaultModalClose! : () => setLocalVaultModalOpen(false);
  const handleVaultModalOpen = isControlled ? () => { } : () => setLocalVaultModalOpen(true);

  const memberNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    if (wizardData) map.set(wizardData.id, wizardData.name);
    if (apprenticeData) map.set(apprenticeData.id, apprenticeData.name);
    if (soldiersData?.soldiers) {
      for (const s of soldiersData.soldiers) {
        map.set(s.id, s.name);
      }
    }
    return map;
  }, [wizardData, apprenticeData, soldiersData]);

  const unequippedVaultItems = React.useMemo(
    () => vaultItems.filter((item) => !item.warbandMemberId),
    [vaultItems]
  );

  const categorisedItems = React.useMemo(() => {
    const groups: Record<ItemCategory, VaultItemDTO[]> = {
      general: [],
      scrolls: [],
      potions: [],
      grimoires: [],
    };
    for (const item of unequippedVaultItems) {
      groups[categoriseItem(item)].push(item);
    }

    groups.general.sort((a, b) => a.name.localeCompare(b.name));
    groups.scrolls.sort((a, b) => a.name.localeCompare(b.name));
    groups.potions.sort((a, b) => a.name.localeCompare(b.name));
    groups.grimoires.sort((a, b) => a.name.localeCompare(b.name));

    return groups;
  }, [unequippedVaultItems]);

  const handleDiscard = React.useCallback((instanceId: string) => {
    updateVaultItems(vaultItems.filter((i) => i.instanceId !== instanceId).map((i) => i.id));
  }, [vaultItems, updateVaultItems]);

  const handleSell = React.useCallback((instanceId: string) => {
    sellVaultItem(instanceId);
  }, [sellVaultItem]);

  return (
    <Box>

      {unequippedVaultItems.length === 0 ? (
        <Box>
          <SectionHeading label={`Vault Contents (${unequippedVaultItems.length})`} />
          <Typography color="textSecondary">The vault is empty.</Typography>
        </Box>
      ) : (
        <Box>
          <SectionHeading label={`Vault Contents (${unequippedVaultItems.length})`} />
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              variant="fullWidth"
            >
              {CATEGORIES.map((cat) => {
                const count = categorisedItems[cat.key].length;
                return (
                  <Tab
                    key={cat.key}
                    label={`${cat.label} (${count})`}
                  />
                );
              })}
            </Tabs>
          </Box>
          {CATEGORIES.map((cat, i) => {
            const items = categorisedItems[cat.key];
            const { Card, MiniCard } = cat;
            return (
              <Box key={cat.key} sx={{ display: activeTab === i ? 'block' : 'none', pt:3 }}>
                {items.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No items in this category.
                  </Typography>
                ) : isMinified ? (
                  <Box sx={{ ...miniFlexSx, alignItems: cat.key === 'potions' ? 'flex-end' : 'flex-start' }}>
                    {items.map((item) => (
                      <MiniCard
                        key={item.instanceId}
                        item={item}
                        memberNameById={memberNameById}
                        onDiscard={handleDiscard}
                        onSell={item.salePrice > 0 ? handleSell : undefined}
                      />
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ ...miniFlexSx, alignItems: cat.key === 'potions' ? 'flex-end' : 'flex-start' }}>
                    {items.map((item) => (
                      <Card
                        key={item.instanceId}
                        item={item}
                        memberNameById={memberNameById}
                        onDiscard={handleDiscard}
                        onSell={item.salePrice > 0 ? handleSell : undefined}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {inlineAddButton ? (
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          aria-label="Add to Vault"
          onClick={() => handleVaultModalOpen()}
          disabled={!selectedWarbandId}
          sx={{ width: 'fit-content' }}
        >
          Add to Vault
        </Button>
      ) : !isControlled ? (
        <Fab
          variant="extended"
          color="primary"
          size="medium"
          aria-label="Add to Vault"
          onClick={() => handleVaultModalOpen()}
          disabled={!selectedWarbandId}
          sx={{ position: 'fixed', bottom: 56, right: 16 }}
        >
          <AddIcon />
          Add to Vault
        </Fab>
      ) : null}
      <ManageVaultModal
        open={vaultModalOpen}
        onClose={handleVaultModalClose}
      />
    </Box>
  );
});
